import { isPlainObject } from 'lodash-es';
import React, { Fragment, ReactNode } from 'react';

import { Link } from '@sbu/components.link';

import { DownloadLink } from 'shared/components/internal/DownloadLink';
import { Moment } from 'shared/components/internal/Moment';

import { RecordType } from '../shared';
import { MatchedItem } from './MatchedItem';
import {
  broDnsAnswer,
  broFtpCmdarg,
  broFtpDataChannel,
  BroRecordType,
  broSmbCommand,
  broSmbFile,
  broSmbMapping,
  broSmbTree,
  broSmtpHeaders,
  matchedMeta,
  matchedUserAudit,
  SubObjectType,
  Sv3RecordType,
} from './types';

interface MapValueType {
  [name: string]: any;
}

type RendererType = (value: any, record?: RecordType) => ReactNode;

type RendererMapType = {
  [key in Sv3RecordType | BroRecordType | SubObjectType]: RendererType;
};

interface RecordTypes {
  [name: string]: Sv3RecordType | BroRecordType | SubObjectType;
}

// We filter these on the overview panel, they're still available in full details
const filteredProperties = ['id', 'active'];
const emptyValues = [null, undefined, ''];

/**
 * Render a property object, where each will include the property name and value.
 * @param value - The record property value
 * @param name - The record property name
 * @param renderer - See the `renderer` parameter for `mapRenderer` function
 */
const propertyRenderer = (
  value: any,
  name: string,
  renderer: RendererType | RecordTypes
) => {
  if (filteredProperties.includes(name)) {
    return null;
  }

  if (emptyValues.includes(value)) {
    return null;
  }

  const rendererFn: RendererType | undefined = isPlainObject(renderer)
    ? getRenderer(name, renderer[name])
    : (renderer as RendererType);

  return (
    <div key={`record-property-${name}`}>
      {name && !isPlainObject(value) ? `${name}: ` : ''}
      {rendererFn ? rendererFn(value) : value}
    </div>
  );
};

/**
 * Render a map of Record properties, with support for nested properties.
 * @param value - Enumerable object of Record properties
 * @param renderer - The renderer function for all record property values in the
 * map, or a map containing record types (keyed off the record property name)
 * used to determine the nested property renderer.
 */
const mapRenderer = (
  value: MapValueType,
  renderer: RendererType | RecordTypes
) => {
  if (Array.isArray(value)) {
    return value.map((item) => mapRenderer(item, renderer));
  }

  if (isPlainObject(value)) {
    return (
      <Fragment>
        {Object.entries(value).map(([name, value]) =>
          propertyRenderer(value, name, renderer)
        )}
      </Fragment>
    );
  }

  return propertyRenderer(value, '', renderer);
};

/**
 * Get the render function for the given property type.
 * @param propType - The name of the property type as a string.
 * @param map - Optional. Defaults to `rendererMap`. A map containing renderers
 * keyed off the property type. This can also be passed as a string of the
 * renderer type.
 */
const getRenderer = (
  propType?: string,
  map: string | RendererMapType = rendererMap
): RendererType | undefined =>
  typeof map === 'string'
    ? rendererMap[map]
    : propType && map.hasOwnProperty(propType) && map[propType];

/**
 * Render an array of strings
 * @param value - array of strings, with fallback support for non-arrays.
 */
const arrayStringRenderer = (value: any) =>
  Array.isArray(value) ? value.join(', ') : value;

/**
 * Render Boolean values
 * @param value - Expected to be a string with the value being either "true" or
 * "false", fallback support for truthy evaluation.
 */
const booleanRenderer = (value: string | boolean) => {
  const isTrue = value === true || value === 'true';
  const isFalse = value === false || value === 'false';

  return isTrue ? 'True' : isFalse ? 'False' : value;
};

/**
 * Render float values
 * @param value - Float value as a string
 * @returns The parsed float value in the locale string. If a number cannot be
 * parsed from the argument, then null is returned.
 */
const floatRenderer = (value: string) => {
  const parsedValue = parseFloat(value);

  return isNaN(parsedValue) ? null : parsedValue.toLocaleString();
};

/**
 * Parse integer values
 * @param value - Integer value as a string, with an expected radix of 10.
 * @returns The parsed integer value in the locale string. If the number cannot
 * be parsed from the argument, then null is returned.
 */
const intRenderer = (value: string) => {
  const parsedValue = parseInt(value, 10);

  return isNaN(parsedValue) ? null : parsedValue.toLocaleString();
};

/**
 * Render unformatted values.
 * @param value - Numeric value
 */
const unformattedIntRenderer = (value: number) => value;

/**
 * Render long unformatted values.
 * @param value - Long value
 */
const longRenderer = unformattedIntRenderer;

/**
 * Render timestamp values
 * @param value - The timestamp in long format, expected to be the UNIX
 *                timestamp (ms from Epoch)).
 */
const timestampRenderer = (value: any) =>
  typeof value === 'number' ? <Moment>{value}</Moment> : value;

/**
 * Render map of float values.
 * @param value - Parsable map containing float values
 */
const mapDoubleRenderer = (value: MapValueType) =>
  mapRenderer(value, floatRenderer);

/**
 * Render map of long values.
 * @param value - Parsable map containing long values
 */
const mapLongRenderer = (value: MapValueType) =>
  mapRenderer(value, longRenderer);

/**
 * Render map of string values.
 * @param value - Parsable map containing string values
 */
const mapStringRenderer = (value: MapValueType) =>
  mapRenderer(value, stringRenderer);

/**
 * Render map of string values to an array containing string values.
 * @param value - Parsable map containing arrays of string values
 */
const mapStringArrayStringRenderer = (value: MapValueType) =>
  mapRenderer(value, arrayStringRenderer);

/**
 * Render string value
 * @param value - Renderable value of type string
 */
const stringRenderer = (value: string) => value;
/**
 * Render string value in uppercase
 * @param value - Renderable value of type string in uppercase
 */
const uppercaseStringRenderer = (value: any) =>
  typeof value === 'string' ? value.toUpperCase() : value;

/**
 * Render map of Bro FTP Cmdarg values.
 * @param value - Parsable map containing Bro FTP Cmdarg values
 */
const broFtpCmdargRenderer = (value: MapValueType) =>
  mapRenderer(value, broFtpCmdarg);

/**
 * Render map of Bro DNS answer values.
 * @param value - Parsable map containing Bro DNS answer values
 */
const broDnsAnswersRenderer = (value: MapValueType) =>
  mapRenderer(value, broDnsAnswer);

/**
 * Render map of Bro FTP Data Channel values.
 * @param value - Parsable map containing Bro FTP Data Channel values
 */
const broFtpDataChannelRenderer = (value: MapValueType) =>
  mapRenderer(value, broFtpDataChannel);

/**
 * Render map of Bro SMB Command values.
 * @param value - Parsable map containing Bro SMB Command values
 */
const broSmbCommandRenderer = (value: MapValueType) =>
  mapRenderer(value, broSmbCommand);

/**
 * Render map of Bro SMB File values.
 * @param value - Parsable map containing Bro SMB File values
 */
const broSmbFileRenderer = (value: MapValueType) =>
  mapRenderer(value, broSmbFile);

/**
 * Render map of Bro SMB Mapping values.
 * @param value - Parsable map containing Bro SMB Mapping values
 */
const broSmbMappingRenderer = (value: MapValueType) =>
  mapRenderer(value, broSmbMapping);

/**
 * Render map of Bro SMB Tree values.
 * @param value - Parsable map containing Bro SMB Tree values
 */
const broSmbTreeRenderer = (value: MapValueType) =>
  mapRenderer(value, broSmbTree);

/**
 * Render map of Bro SMTP Header values.
 * @param value - Parsable map containing Bro SMTP Header values
 */
const broSmtpHeadersRenderer = (value: MapValueType) =>
  mapRenderer(value, broSmtpHeaders);

/**
 * Render an array of Bro Record types
 * @param values - Array of Bro Record values, with fallback support for non-arrays
 * @param renderer - The render function for the given Bro Record
 */
const arrayBroRecordHandler = (values: any, renderer: RendererType) =>
  Array.isArray(values)
    ? values.map((value: any) => (
        <div key={`bro-record-${value}`}>{renderer(value)}</div>
      ))
    : values;

/**
 * Render an array of Bro FTP Cmdarg Record values
 * @param values - Array of Bro FTP Cmdarg Record values, with fallback support for non-arrays
 * @param renderer - The render function for the given Bro Record
 */
const arrayBroFtpCmdargRenderer = (value: any) =>
  arrayBroRecordHandler(value, broFtpCmdargRenderer);

/**
 * Render map of Matched Item values.
 * @param value - Parsable map containing Matched Item values
 */
const arrayMatchedItemRenderer = (value: any) => {
  let val = value;

  if (typeof val === 'string') {
    val = JSON.parse(val);
  }

  // TODO: Return null should never happen
  return Array.isArray(val)
    ? val.map((item) => <MatchedItem item={item} key={item.id} />)
    : null;
};

/**
 * Render map of Matched User Audit values.
 * @param value - Parsable map containing Matched User Audit values
 */
const matchedUserAuditRenderer = (value: any) =>
  mapRenderer(value, matchedUserAudit);

/**
 * Render map of Matched Metadata values.
 * @param value - Parsable map containing Matched Metadata values
 */
const matchedMetaRenderer = (value: any) => mapRenderer(value, matchedMeta);

/**
 * Render an array of Bro DNS Answer Record values
 * @param values - Array of Bro DNS Answer Record values, with fallback support for non-arrays
 * @param renderer - The render function for the given Bro Record
 */
const arrayBroDnsAnswerRenderer = (value: any) =>
  arrayBroRecordHandler(value, broDnsAnswersRenderer);

/**
 * Render a stored file that the user can download
 * @param value - The file path of the stored file
 */
function storedFilePathRenderer(value: any) {
  return (
    <DownloadLink
      downloadPath={`/api/v1/extracted-file?filename=${value}`}
      fileName={value}
      key='file-download'
    >
      Download File
    </DownloadLink>
  );
}

/**
 * Render a link to the mapping based on the metadata_mapperUid value
 * @param record - The file path of the stored file
 */
const mappingLinkRenderer = (value: any, record?: RecordType) => (
  <>
    {!!record?.metadata_mapperUid && (
      <Link
        href={`/sec/config/incoming-data/mappings/mapping/${record.metadata_mapperUid}`}
      >
        {value}
      </Link>
    )}
    {!record?.metadata_mapperUid && value}
  </>
);

export const rendererMap: RendererMapType = {
  arrayBroDnsAnswer: arrayBroDnsAnswerRenderer,
  arrayBroFtpCmdarg: arrayBroFtpCmdargRenderer,
  arrayMatchedItem: arrayMatchedItemRenderer,
  arrayString: arrayStringRenderer,
  boolean: booleanRenderer,
  broFtpCmdarg: broFtpCmdargRenderer,
  broFtpDataChannel: broFtpDataChannelRenderer,
  broSmbCommand: broSmbCommandRenderer,
  broSmbFile: broSmbFileRenderer,
  broSmbMapping: broSmbMappingRenderer,
  broSmbTree: broSmbTreeRenderer,
  broSmtpHeaders: broSmtpHeadersRenderer,
  double: floatRenderer,
  float: floatRenderer,
  int: intRenderer,
  long: longRenderer,
  mapDouble: mapDoubleRenderer,
  mapLong: mapLongRenderer,
  mapString: mapStringRenderer,
  mapStringArrayString: mapStringArrayStringRenderer,
  matchedMeta: matchedMetaRenderer,
  matchedUserAudit: matchedUserAuditRenderer,
  metadata_mapperName: mappingLinkRenderer,
  storedFile: storedFilePathRenderer,
  string: stringRenderer,
  timestamp: timestampRenderer,
  unformattedInt: unformattedIntRenderer,
  uppercaseString: uppercaseStringRenderer,
};
